import { useState, type FormEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalDropdown } from './GlobalDropdown';
import { GlobalInput } from './GlobalInput';
import { Favicon } from '../utils/favicon';
import {
  BANK_ACCOUNT_KIND_OPTIONS,
  CURRENCY_OPTIONS,
} from '../lib/bankAccount';
import type { BankAccount, BankAccountKind, CurrencyCode } from '../types';

export type ManualBankAccountFormValues = {
  name: string;
  websiteUrl?: string;
  balance: number;
  ibanMasked?: string;
  accountNumber?: string;
  kind: BankAccountKind;
  currency: CurrencyCode;
};

type Props = {
  saving?: boolean;
  error?: string | null;
  /** When provided, prefills the form for editing an existing manual account. */
  account?: Pick<
    BankAccount,
    'name' | 'websiteUrl' | 'balance' | 'ibanMasked' | 'accountNumber' | 'kind' | 'currency'
  >;
  submitLabel?: string;
  onSubmit: (values: ManualBankAccountFormValues) => void;
  onCancel: () => void;
};

export function ManualBankAccountForm({
  saving = false,
  error,
  account,
  submitLabel = 'Add account',
  onSubmit,
  onCancel,
}: Props) {
  const [name, setName] = useState(account?.name ?? '');
  const [kind, setKind] = useState<BankAccountKind>(account?.kind ?? 'account');
  const [currency, setCurrency] = useState<CurrencyCode>(account?.currency ?? 'EUR');
  const [websiteUrl, setWebsiteUrl] = useState(account?.websiteUrl ?? '');
  const [balance, setBalance] = useState(
    account && Number.isFinite(account.balance) ? String(account.balance) : ''
  );
  const [accountNumber, setAccountNumber] = useState(account?.accountNumber ?? '');
  const [ibanMasked, setIbanMasked] = useState(account?.ibanMasked ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setValidationError('Account name is required.');
      return;
    }

    const parsedBalance = Number(balance);
    if (!Number.isFinite(parsedBalance)) {
      setValidationError('Enter a valid balance.');
      return;
    }

    setValidationError(null);
    onSubmit({
      name: trimmedName,
      websiteUrl: websiteUrl.trim() || undefined,
      balance: parsedBalance,
      ibanMasked: ibanMasked.trim() || undefined,
      accountNumber: accountNumber.trim() || undefined,
      kind,
      currency,
    });
  }

  const displayError = validationError ?? error;
  const balanceLabel = `Balance (${currency})`;

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Account name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="e.g. Business checking"
        autoComplete="off"
        required
      />

      <GlobalDropdown
        label="Type"
        value={kind}
        onChange={(value) => setKind(value as BankAccountKind)}
        options={BANK_ACCOUNT_KIND_OPTIONS}
      />

      <GlobalDropdown
        label="Currency"
        value={currency}
        onChange={(value) => setCurrency(value as CurrencyCode)}
        options={CURRENCY_OPTIONS}
      />

      <GlobalInput
        label="Bank website"
        value={websiteUrl}
        onChange={(event) => setWebsiteUrl(event.target.value)}
        placeholder="e.g. commerzbank.de"
        autoComplete="off"
      />
      <p className="muted" style={{ margin: '-8px 0 0', fontSize: '0.8125rem' }}>
        Used for the favicon shown in your account list.
      </p>

      {websiteUrl.trim() ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Favicon
            websiteUrl={websiteUrl}
            size={24}
            alt=""
            className="companyFavicon"
          />
          <span className="muted" style={{ fontSize: '0.875rem' }}>
            Favicon preview
          </span>
        </div>
      ) : null}

      <GlobalInput
        label={balanceLabel}
        type="number"
        inputMode="decimal"
        step="0.01"
        value={balance}
        onChange={(event) => setBalance(event.target.value)}
        placeholder="0.00"
        required
      />

      <GlobalInput
        label="Account number (optional)"
        value={accountNumber}
        onChange={(event) => setAccountNumber(event.target.value)}
        placeholder="e.g. 0532013000"
        autoComplete="off"
      />

      <GlobalInput
        label="IBAN (optional)"
        value={ibanMasked}
        onChange={(event) => setIbanMasked(event.target.value)}
        placeholder="DE89 3704 0044 0532 0130 00"
        autoComplete="off"
      />

      {displayError ? <p className="sidebarFormError">{displayError}</p> : null}

      <div className="sidebarFormActions">
        <GlobalButton type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </GlobalButton>
        <GlobalButton type="submit" variant="primary" isLoading={saving} disabled={saving}>
          {submitLabel}
        </GlobalButton>
      </div>
    </form>
  );
}
